/**
 * Calculates and prepares all data needed for the Add-Ons and Cancellations chart
 * @param {Array} data - The historical data from the API
 * @param {Object} options - Additional options like height, chartHeight
 * @returns {Object} - All data needed for rendering the Add-Ons and Cancellations in a BlankCard
 */
export const createAddOnCancellations = (data, options = {}) => {
  const {
    height = 350,
    chartHeight = 250,
  } = options

  // Process data to create add-on and cancellation statistics
  const processAddOnCancellationData = () => {
    // Initialize counters
    let totalAddOns = 0
    let totalCancellations = 0
    let totalNoShows = 0
    let totalScheduled = 0
    let uniqueDates = 0
    
    // Calculate daily averages and totals
    data.forEach(day => {
      if (!day.date) return
      
      uniqueDates++;
      totalAddOns += day.addOns || 0
      totalCancellations += day.cancellations || 0
      totalNoShows += day.noShows || 0
      
      // Estimate total scheduled from completed + cancellations + no-shows
      const completed = day.patient_volumes?.completed || 0
      const dailyScheduled = completed - (day.addOns || 0)
      totalScheduled += dailyScheduled
    })
    
    // Avoid division by zero
    uniqueDates = Math.max(1, uniqueDates)
    
    // Daily averages
    const avgAddOns = totalAddOns / uniqueDates
    const avgCancellations = totalCancellations / uniqueDates
    const avgNoShows = totalNoShows / uniqueDates
    const avgScheduled = totalScheduled / uniqueDates
    
    // Calculate the gap between cancellations/no-shows and add-ons
    const combinedCancellations = avgCancellations + avgNoShows
    const gap = combinedCancellations - avgAddOns
    
    // Calculate the percentage of additional bookings needed to balance
    const additionalBookingPercentage = gap > 0 ? (gap / avgScheduled) * 100 : 0

    return {
      avgAddOns: avgAddOns,
      avgCancellations: avgCancellations,
      avgNoShows: avgNoShows,
      avgScheduled: avgScheduled,
      combinedCancellations: combinedCancellations,
      gap: gap,
      additionalBookingPercentage: additionalBookingPercentage
    }
  }
  
  const addOnCancellationData = processAddOnCancellationData()
  
  // Get status based on rates
  const getStatus = () => {
    const combinedRate = addOnCancellationData.cancellationRate + addOnCancellationData.noShowRate
      
    if (combinedRate < 10) {
      return { 
        status: "good", 
        badge: "Normal" 
      }
    }
    return { 
      status: "critical", 
      badge: "Needs Attention" 
    }
  }

  // Create labels for the chart
  const labels = [
    "Add-Ons",
    "Cancellations / No-Shows",
    "Regularly Scheduled"
  ]
  
  // Create legend for BlankCard
  const legend = [
    { label: labels[0], color: "#AEA2FF" },  // Primary blue
    { label: labels[1], color: "#352D66" },  // Red for cancellations
    { label: labels[2], color: "#6955EA" }   // Orange for no-shows
  ]
  
  const statusInfo = getStatus()
  return {
    title: "Schedule Changes",
    description: addOnCancellationData.gap > 0 
      ? `To offset cancellations, try booking ~${addOnCancellationData.gap.toFixed(1)} more patients than your current goal`
      : `Add-ons and cancellations are well balanced. No additional bookings needed.`,
    chartType: "donut",
    badge: statusInfo.badge,
    status: statusInfo.status,
    // Pass data as an array of values
    chartData: [
      parseFloat(addOnCancellationData.avgAddOns.toFixed(1)), 
      parseFloat((addOnCancellationData.avgCancellations + addOnCancellationData.avgNoShows).toFixed(1)), 
      parseFloat(addOnCancellationData.avgScheduled.toFixed(1))
    ],
    // Pass labels as an option
    options: {
      labels: labels
    },
    chartColors: ["#AEA2FF", "#352D66", "#6955EA"],
    chartHeight,
    height,
    stats: {
      avgDailyAddOns: addOnCancellationData.avgAddOns.toFixed(1),
      avgDailyCancellations: (addOnCancellationData.avgCancellations + addOnCancellationData.avgNoShows).toFixed(1)
    },
    smallStats: true,
    legend: legend // Add legend for BlankCard to use
  }
}

export default createAddOnCancellations 